// Merchant Sales Report (MM-3037): reporting-period rules. Dates are plain YYYY-MM-DD calendar days
// (the backend buckets by Kabul day), so everything here is calendar arithmetic, never instants.
import dayjs from "dayjs";
import { DateRange } from "./types";

export const MAX_RANGE_DAYS = 30;
/** The report opens on "Last 7 Days". */
export const DEFAULT_RANGE_DAYS = 7;
export const API_DATE_FORMAT = "YYYY-MM-DD";

export type PresetKey = "yesterday" | "last3" | "last7";

export const PRESETS: { key: PresetKey; label: string; days: number }[] = [
  { key: "yesterday", label: "Yesterday", days: 1 },
  { key: "last3", label: "Last 3 Days", days: 3 },
  { key: "last7", label: "Last 7 Days", days: 7 },
];

/** Inclusive day count; 0 or less when start is after end. */
export const rangeDays = (start: string, end: string) => dayjs(end).diff(dayjs(start), "day") + 1;

/**
 * Presets end on the last day the nightly snapshot covers (data_as_of, normally yesterday), so
 * "Last 7 Days" never includes days that have no data yet.
 */
export const presetRange = (days: number, dataAsOf: string): DateRange => ({
  start: dayjs(dataAsOf).subtract(days - 1, "day").format(API_DATE_FORMAT),
  end: dataAsOf,
});

export const defaultRange = (dataAsOf: string) => presetRange(DEFAULT_RANGE_DAYS, dataAsOf);

export const matchingPreset = (range: DateRange, dataAsOf: string): PresetKey | null =>
  PRESETS.find((preset) => {
    const candidate = presetRange(preset.days, dataAsOf);
    return candidate.start === range.start && candidate.end === range.end;
  })?.key ?? null;

/** Validation message for a picked range, or null when it can be applied. */
export const validateRange = (start: string | null, end: string | null, dataAsOf: string | null): string | null => {
  if (!start || !end) return "Select both a from and a to date";
  const days = rangeDays(start, end);
  if (days < 1) return "From date must be on or before the to date";
  if (days > MAX_RANGE_DAYS) return `Date range cannot exceed ${MAX_RANGE_DAYS} days`;
  if (dataAsOf && dayjs(end).isAfter(dayjs(dataAsOf), "day")) {
    return `Data is available until ${formatDisplayDate(dataAsOf)}`;
  }
  return null;
};

/** "03 JAN 2026", as in the design's header card. */
export const formatDisplayDate = (date: string) => dayjs(date).format("DD MMM YYYY").toUpperCase();

/** "03 JAN 2026 – 09 JAN 2026 (7 DAYS)" */
export const formatPeriod = (range: DateRange) => {
  const days = rangeDays(range.start, range.end);
  return `${formatDisplayDate(range.start)} – ${formatDisplayDate(range.end)} (${days} ${days === 1 ? "DAY" : "DAYS"})`;
};
